import React, { useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Button, Col, Flex, Form, Radio, Row, Select } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useRef } from "react";
import RequiredLabel from "../../../../../common/required-label/RequiredLabel";
import { useTranslation } from "react-i18next";
import { userRoles } from "../../../../../../constants";
import { useState } from "react";
import BookAppointmentDialog from "./BookAppointmentDialog";
import "./Calendar.scss";
import { getServiceProviderAppointmentsAction } from "../../../../../../redux/services/service-provider-appointments/serviceProviderAppointmentsServices";
import { useDispatch, useSelector } from "react-redux";
import useDebounce from "../../../../../../hooks/useDebounce";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import Actions from "../../../../../../redux/actions";
import dayjs from "dayjs";
import { getStaffSelectListAction } from "../../../../../../redux/services/staff/staffServices";
import { getServicesSelectListAction } from "../../../../../../redux/services/services/servicesServices";
import AppointmentDetailsDialog from "./AppointmentDetailsDialog";
import useLocalStorage from "../../../../../../hooks/useLocalStorage";
import { appConfig } from "../../../../../../constants/config/appConfig";
import { getUserProfileAction } from "../../../../../../redux/services/auth/authServices";
import Spinner from "../../../../../common/spinner/Spinner";
import { toastService } from "../../../../../common/toast-messages/toast";

const Calendar = () => {
  const calendarRef = useRef(null);

  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  const { userSession, setUserSession } = useLocalStorage(
    appConfig.SESSION_USER_INFO
  );

  const [tabMode, setTabMode] = useState("timeGridWeek");

  /* dialog dependencies */
  const [openBookAppointmentDialog, setOpenBookAppointmentDialog] =
    useState(false);
  const [
    openViewAppointmentDetailsDialog,
    setOpenViewAppointmentDetailsDialog,
  ] = useState(false);
  /* dialog dependencies */

  /* form dependencies */
  const [form] = Form.useForm();
  const service = Form.useWatch("service", { form, preserve: true });
  const staff = Form.useWatch("staff", { form, preserve: true });
  const staffType = Form.useWatch("staff_type", { form, preserve: true });
  /* form dependencies */

  const { serviceProviderAppointments, serviceProviderAppointmentsInfo } =
    useSelector((state) => state.serviceProviderAppointments);
  const [searchValue] = useState("");
  const [pagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: 500,
  });
  /* table dependencies */

  /* filter dependencies */
  const { debouncedValue: debouncedSearchValue } = useDebounce(
    searchValue,
    500
  );
  const { staffSelectList } = useSelector((state) => state.staff);
  const { servicesSelectList } = useSelector((state) => state.services);
  const [selectedCountryObj] = useState(null);
  /* filter dependencies */

  const selectListApiCalls = () => {
    // service provider / staff select list
    const dataToBeSentForStaffSelectList = {
      page: pagination?.pageNo,
      limit: pagination?.pageSize,
    };
    userSession?.userRole === userRoles?.SERVICE_PROVIDER_ADMIN &&
      dispatch(
        getStaffSelectListAction(dataToBeSentForStaffSelectList, setLoading)
      );

    // services select list
    const dataToBeSentForServicesSelectList = { page: 1, limit: 500 };
    dispatch(
      getServicesSelectListAction(dataToBeSentForServicesSelectList, setLoading)
    );
  };

  useEffect(() => {
    selectListApiCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    userSession &&
      Object.keys(userSession).length !== 0 &&
      userSession !== null &&
      userSession !== undefined &&
      dispatch(getUserProfileAction(setUserSession));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* function definition to get table data */
  const handleGetTableData = () => {
    dispatch(
      Actions.createAction(Actions.GET_SERVICE_PROVIDER_APPOINTMENTS_LIST, {
        serviceProviderAppointments: {},
      })
    );
    setLoading(true);
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
      country: selectedCountryObj,
      // "ASSIGNED_TO_SERVICE_PROVIDER", "APPROVED_BY_SERVICE_PROVIDER",
      // order_status: userSession?.userRole !== userRoles.SERVICE_PROVIDER_ADMIN ? [ 'ASSIGNED_TO_SERVICE_PROVIDER', 'APPROVED_BY_SERVICE_PROVIDER'] : [],
      service,
      staff:
        userSession?.userRole === userRoles.SERVICE_PROVIDER_ADMIN
          ? staff
          : userSession?.userId,
      staffType:
        userSession?.userRole === userRoles.SERVICE_PROVIDER_ADMIN
          ? staffType
          : userSession?.userRole,
      // service_status: ["SCHEDULED", "RECEIVED", "APPROVED", "COMPLETED"],
      service_provider_status:
        userSession?.userRole !== userRoles.SERVICE_PROVIDER_ADMIN
          ? ["APPROVED", "COMPLETED", "REJECTED"]
          : ["NONE", "RECEIVED", "APPROVED", "COMPLETED", "REJECTED"],
      is_payment_completed: "false",
    };
    dispatch(getServiceProviderAppointmentsAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, staff, staffType]);

  const handleAddEditDetails = (element) => {
    let timeStampDate = dayjs(element?.date).unix();
    let currentDateTimeStamp = dayjs().unix();
    if (userSession?.userRole === userRoles.SERVICE_PROVIDER_ADMIN) {
      if (timeStampDate <= currentDateTimeStamp) {
        toastService.error(
          t("Modules.Appointments.pleaseChooseFutureDateAndTimeForAppointment")
        );
        return;
      }
    }
    if (!element?.allDay) {
      dispatch(
        Actions.createAction(Actions.SELECTED_APPOINTMENT_DATE, {
          selectedAppointmentDate: {},
        })
      );
      const date = dayjs(dayjs(element?.date).toISOString());
      const time = dayjs(dayjs(element?.date).toISOString());

      const selectedAppointmentDate = {
        appointmentDate: date,
        appointmentTime: time,
      };
      dispatch(
        Actions.createAction(Actions.SELECTED_APPOINTMENT_DATE, {
          selectedAppointmentDate,
        })
      );
      setOpenBookAppointmentDialog(true);
    }
  };

  const handleViewDetails = (element) => {
    dispatch(
      Actions.createAction(Actions.SELECTED_CALENDAR_APPOINTMENT_ID, {
        selectedCalendarAppointment: {
          selectedCalendarAppointmentId: "",
          service_id: "",
          order_type: "",
        },
      })
    );
    setOpenViewAppointmentDetailsDialog(true);

    if (element && element.event && element.event.extendedProps) {
      const selectedCalendarAppointmentId = element.event.extendedProps._id;
      const selectedAppointmentServiceId =
        element.event.extendedProps.service_id;

      const tempAppointmentObj = serviceProviderAppointmentsInfo?.data?.find(
        (element) => element?._id === selectedCalendarAppointmentId
      );

      dispatch(
        Actions.createAction(Actions.SELECTED_CALENDAR_APPOINTMENT_ID, {
          selectedCalendarAppointment: {
            selectedCalendarAppointmentId,
            service_id: selectedAppointmentServiceId,
            order_type: tempAppointmentObj?.order_type,
          },
        })
      );
    }
  };
  return (
    <div className="appointmentContainer">
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        requiredMark={false}
      >
        <Row gutter={[24]}>
          <Col span={8}>
            <Form.Item
              label={
                <RequiredLabel
                  text={t("Modules.Appointments.service")}
                  required={false}
                />
              }
              name="service"
            >
              <Select
                showSearch
                options={servicesSelectList}
                placeholder={t("Modules.Appointments.selectService")}
                allowClear
              />
            </Form.Item>
          </Col>
          {userSession?.userRole === userRoles.SERVICE_PROVIDER_ADMIN ? (
            <>
              <Col span={8}>
                <Form.Item
                  label={
                    <RequiredLabel
                      text={t("Modules.Appointments.staffType")}
                      required={false}
                    />
                  }
                  name="staff_type"
                >
                  <Select
                    showSearch
                    options={[
                      {
                        value:
                          userRoles.SERVICE_PROVIDER_ADMIN_ROLES.REGULAR_STAFF,
                        label: "Regular",
                      },
                      {
                        value:
                          userRoles.SERVICE_PROVIDER_ADMIN_ROLES.MEDICAL_STAFF,
                        label: "Medical",
                      },
                      {
                        value: userRoles.SERVICE_PROVIDER_ADMIN_ROLES.DOCTOR,
                        label: "Doctor",
                      },
                    ]}
                    placeholder={t("Modules.Appointments.selectStaffType")}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label={
                    <RequiredLabel
                      text={t("Modules.Appointments.staff")}
                      required={false}
                    />
                  }
                  name="staff"
                >
                  <Select
                    showSearch
                    options={staffSelectList}
                    placeholder={t("Modules.Appointments.selectStaff")}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </>
          ) : (
            <></>
          )}
        </Row>
      </Form>
      <div>
        <Flex justify="space-between">
          <Flex gap="1rem" align="center" justify="left">
            <Button
              type="default"
              shape="circle"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={() => {
                let calendarApi = calendarRef.current?.getApi();
                calendarApi.prev();
              }}
            />

            <Button
              type="primary"
              onClick={() => {
                let calendarApi = calendarRef.current?.getApi();
                calendarApi.today();
              }}
            >
              {t("tabs.today")}
            </Button>

            <Button
              type="default"
              shape="circle"
              size="small"
              icon={<ArrowRightOutlined />}
              onClick={() => {
                let calendarApi = calendarRef.current?.getApi();
                calendarApi.next();
              }}
            />
          </Flex>
          <Radio.Group
            onChange={(event) => {
              let calendarApi = calendarRef.current?.getApi();
              calendarApi.changeView(event.target.value);
              setTabMode(event.target.value);
            }}
            value={tabMode}
          >
            <Radio.Button value="dayGridMonth">{t("tabs.month")}</Radio.Button>
            <Radio.Button value="timeGridWeek">{t("tabs.week")}</Radio.Button>
            <Radio.Button value="dayGridDay"> {t("tabs.day")}</Radio.Button>
          </Radio.Group>
        </Flex>
        {loading ? (
          <Spinner />
        ) : (
          <StyledCalendar>
            <FullCalendar
              ref={calendarRef}
              plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
              views={["dayGridMonth", "timeGridWeek", "dayGridDay"]}
              headerToolbar={{
                start: "",
                center: "",
                end: "",
              }}
              events={serviceProviderAppointments}
              dateClick={(element) => handleAddEditDetails(element)}
              eventClick={(element) => handleViewDetails(element)}
            />
          </StyledCalendar>
        )}
      </div>
      {userSession?.userRole === userRoles.SERVICE_PROVIDER_ADMIN ? (
        <BookAppointmentDialog
          open={openBookAppointmentDialog}
          onClose={() => {
            setOpenBookAppointmentDialog(false);
            handleGetTableData();
            setTabMode("timeGridWeek");
          }}
        />
      ) : (
        <></>
      )}
      <AppointmentDetailsDialog
        open={openViewAppointmentDetailsDialog}
        onClose={() => {
          setOpenViewAppointmentDetailsDialog(false);
          handleGetTableData();
          setTabMode("timeGridWeek");
        }}
      />
    </div>
  );
};

const StyledCalendar = ({ children }) => {
  return <div className="calendar-wrapper">{children}</div>;
};

export default Calendar;
